import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, useParams } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  FormControl,
  InputLabel,
  Select as MuiSelect,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel,
  Tooltip
} from '@mui/material';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import DeleteIcon from '@mui/icons-material/Delete';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import BuildIcon from '@mui/icons-material/Build';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import EmailIcon from '@mui/icons-material/Email';
import DescriptionIcon from '@mui/icons-material/Description';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { api } from './api/client';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const teklifTipOptions = [
  { key: 'garanti', label: 'Garanti', icon: <VerifiedUserIcon /> },
  { key: 'not1', label: 'M. Memnuniyeti', icon: <SentimentSatisfiedAltIcon /> },
  {
    key: 'teklif',
    label: 'Teklif',
    icon: <DescriptionIcon />,
    tooltip: 'Müşteri bir talepte bulundu, teklifi sunduk sattık. Şu andaki çoğu teklifimizdeki teklif tipi bu. Teklif oluştur dediğimizde bu seçili gelecek.',
  },
  {
    key: 'yedek_parca',
    label: 'Toplu YP',
    icon: <Inventory2Icon />,
    tooltip: 'Makina satıldı yedek parça listesi oluşturduk, müşteri parçaları seçti, sattık. Yani o anda ihtiyacı yok ama satış oldu. Bu tarz işler için oluşturulan tekliflerde bu teklif türünü seçiyoruz.',
  },
  {
    key: 'sozlesme',
    label: 'B. Sözleşmesi',
    icon: <AssignmentTurnedInIcon />,
    tooltip: 'Yıllık bakım sözleşmesi için teklif oluşturuyorken bu teklif tipini seçiyoruz.',
  },
  {
    key: 'agir_bakim',
    label: 'A. Bakım',
    icon: <BuildIcon />,
    tooltip: 'Bizim fabrikamıza gelen ağır bakım rektifiye işleri için teklif oluşturuyorken seçeceğimiz teklif tipi.',
  },
  {
    key: 'agir_tamir',
    label: 'A. Tamir',
    icon: <BuildIcon />,
    tooltip: 'Sahada veya fabrikamızdaki ağır tamir işleri. Şanzımana değişimi, ana mil değişimi vb. işler için teklif oluşturuyorken bu teklif türünü seçiyoruz.',
  },
  {
    key: 'revizyon_guncelleme',
    label: 'Rev.-Güncelleme',
    icon: <BuildIcon />,
    tooltip: 'Makinada olmayan bir özellik, veya opsiyon ekleme, LXM05 den 32 ye geçme, makinaya alt baskı eklenmesi, enkoder eklenmesi, Vakum jeneratörü eklenmesi gibi işlerde bu teklif türünü seçiyoruz.',
  },
];

const faturaTutariOlmayanTeklifTipleri = ['garanti', 'not1'];
const teklifTipOptionGroups = [
  {
    title: 'Fatura tutarı olmayan',
    keys: faturaTutariOlmayanTeklifTipleri,
    columns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(2, minmax(0, 1fr))' },
  },
  {
    title: 'Fatura tutarı olan',
    keys: teklifTipOptions
      .map((option) => option.key)
      .filter((key) => !faturaTutariOlmayanTeklifTipleri.includes(key)),
    columns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))', md: 'repeat(6, minmax(0, 1fr))' },
  },
];

const getTeklifTipi = (data) => {
  const teklifTipi = data.teklif_tipi || 'teklif';
  return teklifTipOptions.some((option) => option.key === teklifTipi) ? teklifTipi : 'teklif';
};

const teklifTipiToLegacyFields = (teklifTipi) => ({
  not1: teklifTipi === 'not1',
  garanti: teklifTipi === 'garanti',
  rektifiye: teklifTipi === 'rektifiye',
  sozlesme: teklifTipi === 'sozlesme',
  yedek_parca: teklifTipi === 'yedek_parca',
});

const teklifTipTileSx = (active) => ({
  width: '100%',
  minWidth: 0,
  height: 34,
  border: 0,
  borderRadius: '8px',
  bgcolor: active ? '#22c55e' : 'var(--ct-tile-bg, #fff7e6)',
  color: active ? '#fff' : '#394150',
  boxShadow: active ? '0 2px 5px rgba(34, 197, 94, 0.24)' : 'inset 0 0 0 1px rgba(148, 116, 49, 0.26)',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 0.35,
  cursor: 'pointer',
  font: 'inherit',
  padding: '0 4px',
  boxSizing: 'border-box',
  overflow: 'hidden',
  transition: 'background-color 120ms ease, color 120ms ease, box-shadow 120ms ease',
  '& svg': {
    fontSize: 15,
    flex: '0 0 auto',
  },
  '&:hover': {
    bgcolor: active ? '#16a34a' : 'var(--ct-tile-hover, #fff1d1)',
  },
});

const compactToggleSx = (active) => ({
  width: '100%',
  height: 40,
  border: 0,
  borderRadius: '8px',
  bgcolor: active ? '#22c55e' : 'var(--ct-tile-bg, #fff7e6)',
  color: active ? '#fff' : '#394150',
  boxShadow: active ? '0 2px 5px rgba(34, 197, 94, 0.24)' : 'inset 0 0 0 1px rgba(148, 116, 49, 0.26)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 0.45,
  cursor: 'pointer',
  font: 'inherit',
  fontSize: 12.5,
  fontWeight: 700,
  whiteSpace: 'nowrap',
  transition: 'background-color 120ms ease, color 120ms ease, box-shadow 120ms ease',
  '&:hover': {
    bgcolor: active ? '#16a34a' : 'var(--ct-tile-hover, #fff1d1)',
  },
  '& svg': {
    fontSize: 17,
  },
});

const SortableRow = ({ id, item, index, duzenlemeModu, malzemeList, setMalzemeList, updateAdet, updateMaliyetCarpani, updateTarihAraligi, updateAciklama, handleDeleteMaterial }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    backgroundColor: isDragging ? '#fff8e1' : (index % 2 === 0 ? '#eee' : '#fff'),
  };

  return (
    <TableRow ref={setNodeRef} style={style}>
      <TableCell sx={{ p: 0.5, cursor: 'grab', color: '#aaa', width: 32 }} {...attributes} {...listeners}>
        <DragIndicatorIcon fontSize="small" />
      </TableCell>
      <TableCell sx={{ fontSize: '0.8rem', p: 1 }}>{item.malzeme_name}</TableCell>
      <TableCell sx={{ fontSize: '0.8rem', p: 1 }}>{item.product_type}</TableCell>
      <TableCell sx={{ p: 0.5 }}>
        <TextField
          type="number"
          value={item.gereken_adet || ''}
          onChange={(e) => {
            const updatedList = [...malzemeList];
            updatedList[index].gereken_adet = e.target.value;
            updatedList[index].tutar =
              (parseFloat(updatedList[index].fiyat) || 0) *
              (parseFloat(e.target.value) || 0) *
              (parseFloat(updatedList[index].maliyet_carpani) || 1);
            setMalzemeList(updatedList);
          }}
          onBlur={(e) => {
            if (duzenlemeModu && item.id) {
              updateAdet(item.id, e.target.value);
            }
          }}
          size="small"
          sx={{ width: '80px', '& .MuiInputBase-input': { fontSize: '0.8rem', padding: '4px 8px' } }}
        />
      </TableCell>
      <TableCell sx={{ fontSize: '0.8rem', p: 1 }}>{item.birim}</TableCell>
      <TableCell sx={{ fontSize: '0.8rem', p: 1 }}>{parseFloat(item.fiyat || 0).toFixed(2)}</TableCell>
      <TableCell sx={{ p: 0.5 }}>
        <TextField
          type="number"
          value={item.maliyet_carpani === undefined || item.maliyet_carpani === null ? '' : item.maliyet_carpani}
          onChange={(e) => {
            const updatedList = [...malzemeList];
            updatedList[index].maliyet_carpani = e.target.value;
            updatedList[index].tutar =
              (parseFloat(updatedList[index].fiyat) || 0) *
              (parseFloat(updatedList[index].gereken_adet) || 0) *
              (parseFloat(e.target.value) || 1);
            setMalzemeList(updatedList);
          }}
          onBlur={(e) => {
            if (duzenlemeModu && item.id) {
              updateMaliyetCarpani(item.id, e.target.value || 1);
            }
          }}
          size="small"
          inputProps={{ step: 0.1 }}
          placeholder="1"
          sx={{ width: '80px', '& .MuiInputBase-input': { fontSize: '0.8rem', padding: '4px 8px' } }}
        />
      </TableCell>
      <TableCell sx={{ fontSize: '0.8rem', p: 1, fontWeight: 'bold' }}>
        {((parseFloat(item.fiyat) || 0) * (parseFloat(item.gereken_adet) || 0) * (parseFloat(item.maliyet_carpani) || 1)).toFixed(2)}
      </TableCell>
      <TableCell sx={{ p: 0.5 }}>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <Select
            value={{ value: item.teslim_periyot_tipi || 'is_gunu', label: item.teslim_periyot_tipi === 'hafta' ? 'Hafta' : 'İş Günü' }}
            onChange={(selected) => {
              const updatedList = [...malzemeList];
              updatedList[index].teslim_periyot_tipi = selected?.value || 'is_gunu';
              setMalzemeList(updatedList);
              if (duzenlemeModu && item.id) {
                updateTarihAraligi(item.id, 'teslim_tarihi_turu', selected?.value === 'hafta' ? 'hafta' : 'gun');
              }
            }}
            options={[{ value: 'is_gunu', label: 'İş Günü' }, { value: 'hafta', label: 'Hafta' }]}
            styles={{
              container: (base) => ({ ...base, width: '90px', fontSize: '0.75rem' }),
              control: (base) => ({ ...base, minHeight: '28px', height: '28px' }),
              menu: (base) => ({ ...base, fontSize: '0.75rem' }),
            }}
          />
          <TextField
            type="number"
            placeholder="Min"
            value={item.teslim_min || ''}
            onChange={(e) => {
              const updatedList = [...malzemeList];
              updatedList[index].teslim_min = e.target.value;
              setMalzemeList(updatedList);
            }}
            onBlur={(e) => {
              if (duzenlemeModu && item.id) updateTarihAraligi(item.id, 'teslim_tarihi_baslangic', e.target.value);
            }}
            size="small"
            sx={{ width: '50px', '& .MuiInputBase-input': { fontSize: '0.75rem', padding: '4px 6px' } }}
          />
          <span style={{ fontSize: '0.8rem' }}>-</span>
          <TextField
            type="number"
            placeholder="Max"
            value={item.teslim_max || ''}
            onChange={(e) => {
              const updatedList = [...malzemeList];
              updatedList[index].teslim_max = e.target.value;
              setMalzemeList(updatedList);
            }}
            onBlur={(e) => {
              if (duzenlemeModu && item.id) updateTarihAraligi(item.id, 'teslim_tarihi_bitis', e.target.value);
            }}
            size="small"
            sx={{ width: '50px', '& .MuiInputBase-input': { fontSize: '0.75rem', padding: '4px 6px' } }}
          />
        </Box>
      </TableCell>
      <TableCell sx={{ p: 0.5 }}>
        <TextField
          value={item.malzeme_aciklama || ''}
          onChange={(e) => {
            const updatedList = [...malzemeList];
            updatedList[index].malzeme_aciklama = e.target.value;
            setMalzemeList(updatedList);
          }}
          onBlur={(e) => {
            if (duzenlemeModu && item.id) updateAciklama(item.id, e.target.value);
          }}
          size="small"
          sx={{ width: '150px', '& .MuiInputBase-input': { fontSize: '0.8rem', padding: '4px 8px' } }}
        />
      </TableCell>
      <TableCell sx={{ p: 1 }}>
        <IconButton size="small" color="error" onClick={() => handleDeleteMaterial(index)}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};

const IsEmriMalzemeEkle = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { teklif_no: teklifNoPathParam } = useParams();
  const teklifNoParam = searchParams.get('teklif_no') || teklifNoPathParam;
  const [duzenlemeModu, setDuzenlemeModu] = useState(false);
  // Teklif Form State
  const [isEmriData, setIsEmriData] = useState(() => {
    const now = new Date();
    const p = (n) => String(n).padStart(2, '0');
    const teklifNo =
      `${String(now.getFullYear()).slice(2)}${p(now.getMonth() + 1)}${p(now.getDate())}` +
      `${p(now.getHours())}${p(now.getMinutes())}${p(now.getSeconds())}_00`;
    return {
    teklif_no: teklifNo,
    doviz: 'EURO',
    teklif_tarihi: new Date().toISOString().split('T')[0], // Bugünün tarihi YYYY-MM-DD formatında
    musteri: '',
    lokasyon: '',
    makine: '', // Tek makine (HTML formundaki gibi)
    yetkili: '',
    teklif_dili: 'Türkçe',
    musteri_siparis_no: '',
    yetkili_eposta: '',
    yetkili_cep_tel: '',
    oto_mail: 1,
    oto_iptal:1,
    teslim_sekli: 'Nakliye alıcıya ait olacak şekilde.',
    nakliye_firmasi: 'Yurtiçi Kargo',
    odeme_sekli: 'Fatura tarihinden itibaren banka havalesi ile peşin',
    teklif_tipi: 'teklif',
    not2: '',
    aciklama: '',
    };
  });

  // Malzeme Form State
  const [malzeme, setMalzeme] = useState({
    malzeme_name: '',
    product_id: '',
    product_type: '',
    gereken_adet: '',
    birim: '',
    fiyat: '',
    maliyet_carpani: 1,
    maliyet_carpani_secim: '1', // Select için seçilen değer
    teslim_periyot_tipi: 'is_gunu',
    teslim_min: '',
    teslim_max: '',
    malzeme_aciklama: '',
  });

  const selectedTeklifTipi = getTeklifTipi(isEmriData);
  const legacyTeklifFields = teklifTipiToLegacyFields(selectedTeklifTipi);
  const handleTeklifTipiChange = (key) => {
    setIsEmriData({
      ...isEmriData,
      teklif_tipi: key,
    });
    if (duzenlemeModu && isEmriData.teklif_no) {
      api.patch(`/teklifler/${encodeURIComponent(isEmriData.teklif_no)}`, { teklif_tipi: key })
        .catch(() => toast.error('Teklif tipi kaydedilemedi'));
    }
  };

  // Maliyet çarpanı seçenekleri
  const maliyetCarpaniOptions = [
    { value: '1', label: '1' },
    { value: '1.3', label: '1.3' },
    { value: '1.5', label: '1.5' },
    { value: '1.7', label: '1.7' },
    { value: '2', label: '2' },
    { value: '2.5', label: '2.5' },
    { value: '5', label: '5' },
    { value: '6', label: '6' },
    { value: '7', label: '7' },
    { value: 'diger', label: 'Diğer' },
  ];

  // Diğer State'ler
  const [malzemeList, setMalzemeList] = useState([]);
  const [malzemeOption, setMalzemeOption] = useState(null);
  const [musteriler, setMusteriler] = useState([]);
  const [lokasyonlar, setLokasyonlar] = useState([]);
  const [makineler, setMakineler] = useState([]);
  const [personeller, setPersoneller] = useState([]);
  const [dosya1, setDosya1] = useState(null);
  const [dosya2, setDosya2] = useState(null);
  const [dosya3, setDosya3] = useState(null);
  const [openLokasyonModal, setOpenLokasyonModal] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Select value state'leri (controlled components için)
  const [selectedMusteri, setSelectedMusteri] = useState(null);
  const [selectedLokasyon, setSelectedLokasyon] = useState(null);
  const [selectedMakineler, setSelectedMakineler] = useState([]);
  const [yetkililer, setYetkililer] = useState([]);
  const [selectedYetkili, setSelectedYetkili] = useState(null);

  // Yeni Lokasyon Form State
  const [newLokasyon, setNewLokasyon] = useState({
    musteri_id: '',
    ulke: '',
    sehir: '',
    lokasyon_adi: '',
    telefon: '',
    yetkili: '',
    adres: '',
    makineler: [],
  });

  // Debounce hook
  const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);

      return () => {
        clearTimeout(handler);
      };
    }, [value, delay]);

    return debouncedValue;
  };

  // Müşterileri getir
  useEffect(() => {
    const loadData = async () => {
      await fetchMusteriler();
      fetchPersoneller();

      // Eğer URL'de teklif_no varsa, teklif bilgilerini yükle (müşteriler yüklendikten SONRA)
      if (teklifNoParam) {
        fetchTeklifData(teklifNoParam);
      }
    };

    loadData();
  }, [teklifNoParam]);

  const fetchMusteriler = async () => {
    try {
      const { data } = await api.get('/lookups/musteriler', { params: { limit: 10000 } });
      setMusteriler(Array.isArray(data) ? data : data?.data || []);
    } catch (error) {
      console.error('Müşteriler getirilemedi:', error);
      toast.error('Müşteriler getirilemedi');
    }
  };

  const fetchPersoneller = async () => {
    try {
      const { data } = await api.get('/lookups/users', { params: { limit: 2000, scope: 'liste' } });
      setPersoneller(Array.isArray(data) ? data : data?.data || []);
    } catch (error) {
      console.error('Personeller getirilemedi:', error);
    }
  };

  // Teklif verilerini getir (düzenleme modu için) — FastAPI
  const fetchTeklifData = async (teklifNo) => {
    try {
      const { data: teklif } = await api.get(`/teklifler/${encodeURIComponent(teklifNo)}`);
      if (!teklif || !teklif.teklif_no) {
        toast.error('Teklif bulunamadı');
        return;
      }

      const baslik = teklif;
      const urunler = teklif.kalemler || [];

        // Düzenleme modunu aktif et
        setDuzenlemeModu(true);
        const loadedTeklifTipi = baslik.teklif_tipi || 'teklif';

        // Başlık bilgilerini doldur
        setIsEmriData({
          teklif_no: baslik.teklif_no || '',
          doviz: formDoviz(baslik.doviz),
          teklif_tarihi: baslik.teklif_tarihi || '',
          musteri: baslik.musteri || '',
          lokasyon: baslik.lokasyon || '',
          makine: baslik.makine || '',
          yetkili: baslik.yetkili || '',
          teklif_dili: displayDil(baslik.teklif_dili),
          musteri_siparis_no: baslik.musteri_siparis_no || '',
          yetkili_eposta: baslik.yetkili_eposta || '',
          yetkili_cep_tel: baslik.yetkili_cep_tel || '',
          oto_mail: (baslik.otomatik_eposta === true || baslik.otomatik_eposta === 1 || baslik.otomatik_eposta === '1' || baslik.oto_mail == 1 || baslik.oto_mail === '1') ? 1 : 0,
          // '0' truthy olduğu için || kullanma — aksi halde kapalı bayrak yanlışlıkla açık kalır / kaybolur
          oto_iptal: (baslik.otomatik_iptal === true || baslik.otomatik_iptal === 1 || baslik.otomatik_iptal === '1' || baslik.oto_iptal == 1 || baslik.oto_iptal === '1') ? 1 : 0,
          teslim_sekli: baslik.teslim_sekli || '',
          nakliye_firmasi: baslik.nakliye_firmasi || '',
          odeme_sekli: baslik.odeme_sekli || '',
          teklif_tipi: loadedTeklifTipi,
          not2: baslik.notlar_iki || baslik.not2 || '',
          aciklama: baslik.aciklama || '',
        });
        if (baslik.yetkili || baslik.yetkili_eposta || baslik.yetkili_cep_tel) {
          setSelectedYetkili({
            value: `${baslik.yetkili || ''}*${baslik.yetkili_eposta || ''}*${baslik.yetkili_cep_tel || ''}`,
            label: baslik.yetkili || baslik.yetkili_eposta || '',
            yetkili: baslik.yetkili || '',
            eposta: baslik.yetkili_eposta || '',
            telefon: baslik.yetkili_cep_tel || '',
          });
        }

        // Müşteri seçimini ayarla
        if (baslik.musteri) {
          // Backend'den musteri_id geliyor mu kontrol et
          let musteriId = baslik.musteri_id;
          let musteriAd = baslik.musteri;
          let musteriValue;

          if (baslik.musteri.includes('*')) {
            // Eski format: ID*İsim
            [musteriId, musteriAd] = baslik.musteri.split('*');
            musteriValue = baslik.musteri;
          } else if (musteriId) {
            // Yeni format: musteri ve musteri_id ayrı alanlar
            musteriValue = `${musteriId}*${musteriAd}`;
          } else {
            // Sadece isim var, ID yok - müşteriler listesinden bul
            const bulunanMusteri = musteriler.find(m => m.firma === baslik.musteri);
            if (bulunanMusteri) {
              musteriId = bulunanMusteri.musteri_id;
              musteriValue = `${musteriId}*${musteriAd}`;
            } else {
              musteriValue = baslik.musteri;
            }
          }

          setSelectedMusteri({ value: musteriValue, label: musteriAd });
          setIsEmriData((prev) => ({ ...prev, musteri: musteriValue }));

          // Lokasyonları getir (eğer musteriId varsa)
          if (musteriId) {
            const lokResponse = await api.get('/lookups/lokasyonlar', {
              params: { musteri_id: musteriId }
            });
            const lokData = Array.isArray(lokResponse.data) ? lokResponse.data : lokResponse.data?.data || [];
            if (lokData.length) {
              setLokasyonlar(lokData);

              // Lokasyon seçimini ayarla
              if (baslik.lokasyon) {
                let lokId = baslik.lokasyon_id;
                let lokAd = baslik.lokasyon;
                let lokValue;

                if (baslik.lokasyon.includes('*')) {
                  // Eski format: ID*İsim
                  [lokId, lokAd] = baslik.lokasyon.split('*');
                  lokValue = baslik.lokasyon;
                } else if (lokId) {
                  // Yeni format: lokasyon ve lokasyon_id ayrı alanlar
                  lokValue = `${lokId}*${lokAd}`;
                } else {
                  // Sadece isim var - lokasyonlar listesinden bul
                  const bulunanLokasyon = lokData.find(l => l.lokasyon_adi === baslik.lokasyon);
                  if (bulunanLokasyon) {
                    lokId = bulunanLokasyon.lokasyon_id;
                    lokValue = `${lokId}*${lokAd}`;
                  } else {
                    lokValue = baslik.lokasyon;
                  }
                }

                setSelectedLokasyon({ value: lokValue, label: lokAd });
                setIsEmriData((prev) => ({ ...prev, lokasyon: lokValue }));
                if (lokId) fetchYetkililer(lokId, baslik);

                // Makineleri getir (eğer lokId varsa). Seçim, liste boş olsa da kayıttan gelir.
                if (lokId && (baslik.makine || baslik.uyf_no || baslik.seri_no)) {
                  let makData = [];
                  try {
                    const makResponse = await api.get('/lookups/makineler', {
                      params: { lokasyon_id: lokId }
                    });
                    makData = Array.isArray(makResponse.data) ? makResponse.data : makResponse.data?.data || [];
                  } catch (err) {
                    console.error('Makineler getirilemedi:', err);
                  }
                  if (makData.length) setMakineler(makData);

                  let opts = makineOptionsFromBaslik(baslik);
                  if (makData.length) {
                    opts = opts.map((opt) => {
                      const parts = String(opt.value || '').split('*');
                      const name = parts.length >= 3 ? parts.slice(2).join('*') : opt.label;
                      const bulunan = makData.find((m) =>
                        String(m.makine || '').trim() === String(name || '').trim()
                        || (
                          String(m.siparis_no || '') === String(parts[0] || '')
                          && String(m.seri_no || '') === String(parts[1] || '')
                        )
                      );
                      if (!bulunan) return opt;
                      const value = `${bulunan.siparis_no}*${bulunan.seri_no}*${bulunan.makine}`;
                      return {
                        value,
                        label: `${bulunan.siparis_no}-${bulunan.seri_no}-${bulunan.makine}`,
                      };
                    });
                  }
                  if (!opts.length && baslik.makine && !String(baslik.makine).includes('*')) {
                    const names = String(baslik.makine).split(',').map((s) => s.trim()).filter(Boolean);
                    opts = names.map((name) => {
                      const bulunan = makData.find((m) => String(m.makine || '').trim() === name);
                      if (bulunan) {
                        const value = `${bulunan.siparis_no}*${bulunan.seri_no}*${bulunan.makine}`;
                        return {
                          value,
                          label: `${bulunan.siparis_no}-${bulunan.seri_no}-${bulunan.makine}`,
                        };
                      }
                      return { value: `**${name}`, label: name };
                    });
                  }
                  setSelectedMakineler(opts);
                  setIsEmriData((prev) => ({ ...prev, makine: opts.map((o) => o.value).join(',') }));
                }
              }
            }
          }
        }

        // Ürünleri yükle — FastAPI kalemler → malzemeList
        if (urunler && urunler.length > 0) {
          const loadedUrunler = urunler.map((urun) => {
            const maliyetCarpaniValue = urun.digerMaliyet && urun.digerMaliyet !== '' && parseFloat(urun.digerMaliyet) > 0
              ? parseFloat(urun.digerMaliyet)
              : parseFloat(urun.maliyet_carpani) || 1;
            const maliyetCarpaniSecim = urun.digerMaliyet && urun.digerMaliyet !== '' && parseFloat(urun.digerMaliyet) > 0
              ? 'diger'
              : String(urun.maliyet_carpani ?? 1);
            const toplamTutar = urun.calculated_price ?? urun.tutar;
            const birimFiyat = parseFloat(urun.fiyat) || 0;
            return {
              id: urun.id,
              malzeme_name: urun.product_name || urun.urun_adi || '',
              product_brand: urun.product_brand || '',
              product_id: urun.urun_id || '',
              product_type: urun.urun_tipi || '',
              gereken_adet: urun.adet || '',
              birim: urun.birim || 'adet',
              fiyat: Number(birimFiyat).toFixed(3),
              doviz: urun.doviz || '',
              maliyet_carpani: maliyetCarpaniValue,
              maliyet_carpani_secim: maliyetCarpaniSecim,
              teslim_periyot_tipi: urun.teslim_tarihi_turu === 'hafta' ? 'hafta' : 'is_gunu',
              teslim_min: urun.teslim_tarihi_baslangic || '',
              teslim_max: urun.teslim_tarihi_bitis || '',
              malzeme_aciklama: urun.aciklama || '',
              unit_id: urun.unit_id || '',
              ssh_unit_id: urun.ssh_unit_id || '',
              unit_adi: urun.unit_adi || '',
              unit_adet: urun.unit_adet || '',
              is_valid: true,
              tutar: toplamTutar,
            };
          });
          setMalzemeList(loadedUrunler);
        }

        toast.success('Teklif yüklendi');
        return;
    } catch (error) {
      console.error('Teklif getirilemedi:', error);
      // URL'de numara var ama DB'de yok → yeni oluşturma (PATCH yerine POST)
      if (error?.response?.status === 404) {
        setDuzenlemeModu(false);
        setIsEmriData((prev) => ({ ...prev, teklif_no: teklifNo }));
        return;
      }
      const detail = error?.response?.data?.detail || error?.response?.data?.error?.message;
      toast.error(detail || 'Teklif getirilemedi');
    }
  };

  // Müşteri seçildiğinde lokasyonları getir
  const handleMusteriChange = async (selectedOption) => {
    setSelectedMusteri(selectedOption);

    if (!selectedOption) {
      // Müşteri temizlendiğinde tüm ilgili alanları resetle
      setIsEmriData((prev) => ({
        ...prev,
        musteri: '',
        lokasyon: '',
        makine: '',
        yetkili: '',
        yetkili_eposta: '',
        yetkili_cep_tel: '',
      }));
      setSelectedLokasyon(null);
      setSelectedMakineler([]);
      setSelectedYetkili(null);
      setLokasyonlar([]);
      setMakineler([]);
      setYetkililer([]);
      return;
    }

    const musteriId = selectedOption.value.split('*')[0];

    // Müşteri değiştiğinde ilgili alanları resetle
    setIsEmriData((prev) => ({
      ...prev,
      musteri: selectedOption.value,
      lokasyon: '',
      makine: '',
      yetkili: '',
      yetkili_eposta: '',
      yetkili_cep_tel: '',
    }));
    setSelectedLokasyon(null);
    setSelectedMakineler([]);
    setMakineler([]);

    try {
      const { data } = await api.get('/lookups/lokasyonlar', {
        params: { musteri_id: musteriId }
      });
      const lokData = Array.isArray(data) ? data : data?.data || [];
      if (lokData.length) {
        setLokasyonlar(lokData);
        // İlk lokasyonu otomatik seçme kısmını kaldırdım, kullanıcı manuel seçsin
      }
    } catch (error) {
      console.error('Lokasyonlar getirilemedi:', error);
      toast.error('Lokasyonlar getirilemedi');
    }
  };

  // Lokasyon seçildiğinde makineleri getir
  const handleLokasyonChange = async (selectedOption) => {
    setSelectedLokasyon(selectedOption);

    if (!selectedOption) {
      // Lokasyon temizlendiğinde ilgili alanları resetle
      setIsEmriData((prev) => ({
        ...prev,
        lokasyon: '',
        makine: '',
        yetkili: '',
        yetkili_eposta: '',
        yetkili_cep_tel: '',
      }));
      setSelectedMakineler([]);
      setSelectedYetkili(null);
      setMakineler([]);
      setYetkililer([]);
      return;
    }

    const lokasyonId = selectedOption.value.split('*')[0];

    // Lokasyon değiştiğinde ilgili alanları resetle
    setIsEmriData((prev) => ({
      ...prev,
      lokasyon: selectedOption.value,
      makine: '',
      yetkili: '',
      yetkili_eposta: '',
      yetkili_cep_tel: '',
    }));
    setSelectedMakineler([]);
    setSelectedYetkili(null);

    fetchMakineler(lokasyonId);
    fetchYetkililer(lokasyonId);
  };

  const fetchMakineler = async (lokasyonId) => {
    try {
      const { data } = await api.get('/lookups/makineler', {
        params: { lokasyon_id: lokasyonId }
      });
      setMakineler(Array.isArray(data) ? data : data?.data || []);
    } catch (error) {
      console.error('Makineler getirilemedi:', error);
    }
  };

  const fetchYetkililer = async (lokasyonId, savedYetkili = null) => {
    try {
      const { data: yetkililerData } = await api.get(`/lookups/lokasyonlar/${lokasyonId}/yetkili`);
      const row = yetkililerData?.data?.[0] || yetkililerData;
      if (row && row.yetkili) {

        // Tüm yetkililer listesini oluştur
        const yetkiliList = [];

        // Yetkili 1
        if (row.yetkili) {
          yetkiliList.push({
            value: `${row.yetkili}*${row.eposta || ''}*${row.telefon || ''}`,
            label: row.yetkili,
            yetkili: row.yetkili,
            eposta: row.eposta || '',
            telefon: row.telefon || ''
          });
        }

        // Yetkili 2-9 (yetkili_iki, yetkili_uc, vb.)
        const yetkiliFields = [
          { name: 'yetkili_iki', eposta: 'eposta_iki', telefon: 'telefon_iki' },
          { name: 'yetkili_uc', eposta: 'eposta_uc', telefon: 'telefon_uc' },
          { name: 'yetkili_dort', eposta: 'eposta_dort', telefon: 'telefon_dort' },
          { name: 'yetkili_bes', eposta: 'eposta_bes', telefon: 'telefon_bes' },
          { name: 'yetkili_alti', eposta: 'eposta_alti', telefon: 'telefon_alti' },
          { name: 'yetkili_yedi', eposta: 'eposta_yedi', telefon: 'telefon_yedi' },
          { name: 'yetkili_sekiz', eposta: 'eposta_sekiz', telefon: 'telefon_sekiz' },
          { name: 'yetkili_dokuz', eposta: 'eposta_dokuz', telefon: 'telefon_dokuz' }
        ];

        yetkiliFields.forEach(field => {
          if (row[field.name]) {
            yetkiliList.push({
              value: `${row[field.name]}*${row[field.eposta] || ''}*${row[field.telefon] || ''}`,
              label: row[field.name],
              yetkili: row[field.name],
              eposta: row[field.eposta] || '',
              telefon: row[field.telefon] || ''
            });
          }
        });

        setYetkililer(yetkiliList);

        const savedName = String(savedYetkili?.yetkili || '').trim();
        const matched = savedName
          ? yetkiliList.find((item) => item.yetkili === savedName)
          : null;
        const chosen = matched || (savedName ? {
          value: `${savedName}*${savedYetkili.yetkili_eposta || ''}*${savedYetkili.yetkili_cep_tel || ''}`,
          label: savedName,
          yetkili: savedName,
          eposta: savedYetkili.yetkili_eposta || '',
          telefon: savedYetkili.yetkili_cep_tel || '',
        } : yetkiliList[0]);
        if (chosen) {
          setSelectedYetkili(chosen);
          setIsEmriData((prev) => ({
            ...prev,
            yetkili: chosen.yetkili,
            yetkili_eposta: savedName ? (savedYetkili.yetkili_eposta || chosen.eposta) : chosen.eposta,
            yetkili_cep_tel: savedName ? (savedYetkili.yetkili_cep_tel || chosen.telefon) : chosen.telefon,
          }));
        }
      }
    } catch (error) {
      console.error('Yetkililer getirilemedi:', error);
    }
  };


  const formDoviz = (d) => {
    const v = String(d || 'EUR').toUpperCase();
    if (v === 'EUR' || v === 'EURO' || v === '€') return 'EURO';
    if (v === 'TL' || v === 'TRY' || v === 'TRL') return 'TL';
    return 'EURO';
  };

  const mapDoviz = (d) => {
    const v = String(d || 'EUR').toUpperCase();
    if (v === 'EURO' || v === '€') return 'EUR';
    if (['EUR', 'TL', 'USD', 'GBP'].includes(v)) return v;
    return 'EUR';
  };

  const mapDil = (d) => {
    const v = String(d || '').toLowerCase();
    return v.startsWith('en') || v === 'english' || v === 'ingilizce' ? 'EN' : 'TR';
  };

  /** API TR/EN/Turkish → select değerleri Türkçe/English */
  const displayDil = (d) => {
    const v = String(d || '').toLowerCase();
    return v.startsWith('en') || v === 'english' || v === 'ingilizce' ? 'English' : 'Türkçe';
  };

  const dilTicariVarsayilan = (dilLabel) => {
    const en = displayDil(dilLabel) === 'English';
    return en
      ? {
          teslim_sekli: "Shipping is at the buyer's expense. (EXW).",
          nakliye_firmasi: '-',
          odeme_sekli: 'In advance by bank transfer from the invoice date.',
        }
      : {
          teslim_sekli: 'Nakliye alıcıya ait olacak şekilde.',
          nakliye_firmasi: 'Yurtiçi Kargo',
          odeme_sekli: 'Fatura tarihinden itibaren banka havalesi ile peşin',
        };
  };

  /**
   * Select value: `uyf*seri*makineAdi` (PHP uyumlu).
   * Çoklu: dizi veya virgülle birleşik encoded değerler → ayrı kolonlar.
   */
  const parseMakineFields = (raw) => {
    let tokens = [];
    if (Array.isArray(raw)) {
      tokens = raw
        .map((x) => (x && typeof x === 'object' ? x.value : x))
        .filter(Boolean)
        .map(String);
    } else if (raw != null && String(raw).trim()) {
      const s = String(raw).trim();
      // encoded token(lar): "uyf*seri*ad" veya "a*b*c, d*e*f"
      if (s.includes('*')) {
        tokens = s.includes(',')
          ? s.split(',').map((t) => t.trim()).filter(Boolean)
          : [s];
      } else {
        // zaten düz makine adı
        return { makine: s, uyf_no: undefined, seri_no: undefined };
      }
    }
    const uyfs = [];
    const seris = [];
    const names = [];
    for (const tok of tokens) {
      if (tok.includes('*')) {
        const parts = tok.split('*');
        if (parts.length >= 3) {
          uyfs.push(parts[0]);
          seris.push(parts[1]);
          names.push(parts.slice(2).join('*'));
        } else {
          names.push(tok);
        }
      } else {
        names.push(tok);
      }
    }
    return {
      makine: names.length ? names.join(', ') : undefined,
      uyf_no: uyfs.length ? uyfs.join(', ') : undefined,
      seri_no: seris.length ? seris.join(', ') : undefined,
    };
  };

  /** Kayıtlı makine/uyf/seri → react-select (isMulti) options */
  const makineOptionsFromBaslik = (baslik) => {
    const names = String(baslik.makine || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    if (!names.length) return [];
    const uyfs = String(baslik.uyf_no || '')
      .split(',')
      .map((s) => s.trim());
    const seris = String(baslik.seri_no || '')
      .split(',')
      .map((s) => s.trim());
    // Eski bozuk kayıt: makine alanında hâlâ uyf*seri*ad
    if (names.length === 1 && names[0].includes('*')) {
      const parsed = parseMakineFields(names[0]);
      const n = (parsed.makine || '').split(',').map((s) => s.trim()).filter(Boolean);
      const u = (parsed.uyf_no || '').split(',').map((s) => s.trim());
      const se = (parsed.seri_no || '').split(',').map((s) => s.trim());
      return n.map((name, i) => {
        const value = `${u[i] || ''}*${se[i] || ''}*${name}`;
        return { value, label: `${u[i] || ''}-${se[i] || ''}-${name}` };
      });
    }
    return names.map((name, i) => {
      const value = `${uyfs[i] || ''}*${seris[i] || ''}*${name}`;
      return { value, label: `${uyfs[i] || ''}-${seris[i] || ''}-${name}` };
    });
  };

  const buildTeklifPatchPayload = () => {
    const musteriRaw = String(isEmriData.musteri || '');
    const lokRaw = String(isEmriData.lokasyon || '');
    const musteriId = parseInt(musteriRaw.split('*')[0], 10);
    const lokasyonId = parseInt(lokRaw.split('*')[0], 10);
    const musteriAd = musteriRaw.includes('*') ? musteriRaw.split('*').slice(1).join('*') : musteriRaw;
    const lokAd = lokRaw.includes('*') ? lokRaw.split('*').slice(1).join('*') : lokRaw;
    const makineRaw = Array.isArray(selectedMakineler)
      ? selectedMakineler
      : (selectedMakineler?.value || isEmriData.makine);
    const makineFields = parseMakineFields(makineRaw);
    return {
      teklif_tarihi: isEmriData.teklif_tarihi || undefined,
      musteri_id: Number.isFinite(musteriId) ? musteriId : undefined,
      musteri: musteriAd || undefined,
      lokasyon_id: Number.isFinite(lokasyonId) ? lokasyonId : undefined,
      lokasyon: lokAd || undefined,
      makine: makineFields.makine,
      uyf_no: makineFields.uyf_no,
      seri_no: makineFields.seri_no,
      yetkili: isEmriData.yetkili || undefined,
      yetkili_eposta: isEmriData.yetkili_eposta || undefined,
      yetkili_cep_tel: isEmriData.yetkili_cep_tel || undefined,
      teklif_dili: mapDil(isEmriData.teklif_dili),
      musteri_siparis_no: isEmriData.musteri_siparis_no || undefined,
      otomatik_eposta: Boolean(Number(isEmriData.oto_mail)),
      teslim_sekli: isEmriData.teslim_sekli || undefined,
      nakliye_firmasi: isEmriData.nakliye_firmasi || undefined,
      odeme_sekli: isEmriData.odeme_sekli || undefined,
      doviz: mapDoviz(isEmriData.doviz),
      temsilci: localStorage.getItem('ssh_user_name') || undefined,
      notlar_bir: legacyTeklifFields.not1 ? 'İlgili ürünler müşteri memnuniyeti kapsamında gönderiliyor.' : undefined,
      garanti: legacyTeklifFields.garanti ? '1' : undefined,
      sozlesme: Boolean(legacyTeklifFields.sozlesme),
      teklif_tipi: selectedTeklifTipi,
      rektifiye: Boolean(legacyTeklifFields.rektifiye),
      yedek_parca: Boolean(legacyTeklifFields.yedek_parca),
      otomatik_iptal: String(isEmriData.oto_iptal == 1 || isEmriData.oto_iptal === '1' ? '1' : '0'),
      // PHP PDF: fatura==0 → fiyat göster; fatura==1 → irsaliye/garanti (fiyat 0)
      fatura: faturaTutariOlmayanTeklifTipleri.includes(selectedTeklifTipi) ? 1 : 0,
      notlar_iki: isEmriData.not2 || undefined,
      genel_notlar: isEmriData.aciklama || undefined,
    };
  };

  /** Yeni teklif POST — TeklifCreate zorunlu alanlar. */
  const buildTeklifCreatePayload = () => {
    const base = buildTeklifPatchPayload();
    if (!base.musteri_id || !base.musteri || !base.teklif_tarihi) {
      throw new Error('Müşteri ve teklif tarihi zorunludur.');
    }
    return {
      teklif_no: isEmriData.teklif_no,
      teklif_tarihi: base.teklif_tarihi,
      musteri_id: base.musteri_id,
      musteri: base.musteri,
      lokasyon_id: base.lokasyon_id ?? null,
      lokasyon: base.lokasyon ?? null,
      makine: base.makine ?? null,
      uyf_no: base.uyf_no ?? null,
      seri_no: base.seri_no ?? null,
      yetkili: base.yetkili ?? null,
      yetkili_eposta: base.yetkili_eposta ?? null,
      yetkili_cep_tel: base.yetkili_cep_tel ?? null,
      teklif_dili: base.teklif_dili || 'TR',
      musteri_siparis_no: base.musteri_siparis_no ?? null,
      otomatik_eposta: Boolean(base.otomatik_eposta),
      teslim_sekli: base.teslim_sekli ?? null,
      nakliye_firmasi: base.nakliye_firmasi ?? null,
      odeme_sekli: base.odeme_sekli ?? null,
      doviz: base.doviz || 'EUR',
      temsilci: base.temsilci ?? null,
      notlar_bir: base.notlar_bir ?? null,
      garanti: base.garanti ?? null,
      sozlesme: Boolean(base.sozlesme),
      teklif_tipi: base.teklif_tipi || 'teklif',
      rektifiye: Boolean(base.rektifiye),
      yedek_parca: Boolean(base.yedek_parca),
      otomatik_iptal: base.otomatik_iptal ?? '0',
      fatura: base.fatura ?? 0,
      notlar_iki: base.notlar_iki ?? null,
      genel_notlar: base.genel_notlar ?? null,
      kalemler: [],
      is_active: true,
    };
  };

  const saveTeklifBaslik = async () => {
    if (duzenlemeModu) {
      return api.patch(
        `/teklifler/${encodeURIComponent(isEmriData.teklif_no)}`,
        buildTeklifPatchPayload()
      );
    }
    const res = await api.post('/teklifler', buildTeklifCreatePayload());
    setDuzenlemeModu(true);
    return res;
  };

  // Inline update fonksiyonları (düzenleme modu için)
  const updateAdet = async (id, adet) => {
    if (!duzenlemeModu) return;

    try {
      const teklifNo = isEmriData.teklif_no;
      if (!teklifNo) return;
      await api.patch(`/teklifler/${encodeURIComponent(teklifNo)}/kalemler/${id}`, { adet: Number(adet) });
    } catch (error) {
      console.error('Adet güncellenemedi:', error);
      toast.error('Adet güncellenirken hata oluştu');
    }
  };

  const updateMaliyetCarpani = async (id, maliyet_carpani) => {
    if (!duzenlemeModu) return;

    try {
      const teklifNo = isEmriData.teklif_no;
      if (!teklifNo) return;
      // Number() float hassasiyeti bozar; string/Decimal olarak gönder
      await api.patch(`/teklifler/${encodeURIComponent(teklifNo)}/kalemler/${id}`, {
        maliyet_carpani: String(maliyet_carpani).trim(),
      });
    } catch (error) {
      console.error('Maliyet çarpanı güncellenemedi:', error);
      toast.error('Maliyet çarpanı güncellenirken hata oluştu');
    }
  };

  const updateAciklama = async (id, not) => {
    if (!duzenlemeModu) return;

    try {
      const teklifNo = isEmriData.teklif_no;
      if (!teklifNo) return;
      await api.patch(`/teklifler/${encodeURIComponent(teklifNo)}/kalemler/${id}`, { aciklama: not });
    } catch (error) {
      console.error('Açıklama güncellenemedi:', error);
      toast.error('Açıklama güncellenirken hata oluştu');
    }
  };

  const updateTarihAraligi = async (id, column, value) => {
    if (!duzenlemeModu) return;

    try {
      const teklifNo = isEmriData.teklif_no;
      if (!teklifNo) return;
      const payload = {};
      if (column === 'teslim_tarihi_baslangic' || column === 'teslim_min') payload.teslim_tarihi_baslangic = value;
      else if (column === 'teslim_tarihi_bitis' || column === 'teslim_max') payload.teslim_tarihi_bitis = value;
      else if (column === 'teslim_tarihi_turu' || column === 'teslim_periyot_tipi') payload.teslim_tarihi_turu = value;
      else payload[column] = value;
      await api.patch(`/teklifler/${encodeURIComponent(teklifNo)}/kalemler/${id}`, payload);
    } catch (error) {
      console.error('Tarih aralığı güncellenemedi:', error);
      toast.error('Tarih aralığı güncellenirken hata oluştu');
    }
  };

  // Teklif başlık bilgilerini güncelle (düzenleme modu için)
  const updateTeklifBaslik = async () => {
    if (!duzenlemeModu) return;
    try {
      const teklifNo = isEmriData.teklif_no;
      if (!teklifNo) return;
      await api.patch(`/teklifler/${encodeURIComponent(teklifNo)}`, buildTeklifPatchPayload());
      if (dosya1) {
        const fd = new FormData();
        fd.append('dosya', dosya1);
        await api.post(`/teklifler/${encodeURIComponent(teklifNo)}/dosyalar`, fd);
      }
    } catch (error) {
      console.error('Başlık güncellenemedi:', error);
    }
  };

  // Ürün arama fonksiyonu
  const searchProducts = async (inputValue) => {
    if (!inputValue || inputValue.length < 3) {
      return [];
    }

    try {
      const response = await api.get('/mrp/search/products', {
        params: { search_term: inputValue },
      });

      if (response.data && response.data.length > 0) {
        return response.data.map((item) => ({
          id: item.id,
          label: `${item.product_type} - ${item.product_name}`,
          product_name: item.product_name,
          product_type: item.product_type,
          rack_no: item.rack_no || '',
          price: item.default_price || 0,
          currency: item.currency || '',
        }));
      }
      return [];
    } catch (error) {
      console.error('Ürün arama hatası:', error);
      toast.error('Ürün arama sırasında hata oluştu.');
      return [];
    }
  };

  // Malzeme ekleme
  const handleAddMaterial = async () => {
    if (!malzeme.malzeme_name || !malzeme.product_id) {
      toast.error('Lütfen bir ürün seçin.');
      return;
    }

    if (!malzeme.gereken_adet) {
      toast.error('Gereken adet boş olamaz.');
      return;
    }

    if (!malzeme.birim) {
      toast.error('Birim boş olamaz.');
      return;
    }

    // Para birimi kontrolü
    if (!malzeme.doviz || malzeme.doviz === '0' || malzeme.doviz === 0 || malzeme.doviz === 'NaN' || malzeme.doviz === 'nan' || Number.isNaN(malzeme.doviz)) {
      toast.error('Ürünün para birimi yok, kontrol ediniz!');
      return;
    }

    // Fiyat kontrolü (sadece normal ürünler için, UNIT/SSH_UNIT değilse)
    if (malzeme.product_type !== 'unit' && malzeme.product_type !== 'ssh_unit') {
      if (!malzeme.fiyat || parseFloat(malzeme.fiyat) === 0) {
        toast.error('Ürünün fiyatı yok, kontrol ediniz!');
        return;
      }
    }

    // Eğer UNIT veya SSH_UNIT ise, alt malzemeleri patlatarak ekle
    if (malzeme.product_type === 'unit' || malzeme.product_type === 'ssh_unit') {
      try {
        const endpoint = malzeme.product_type === 'unit'
          ? `/mrp/unit/${malzeme.product_id}/materials`
          : `/mrp/ssh_unit/${malzeme.product_id}/materials`;

        const response = await api.get(endpoint);

        if (response.data && response.data.materials) {
          const { hms, ssh_hms, elks, ups } = response.data.materials;
          const newMaterials = [];
          const unitMultiplier = parseFloat(malzeme.gereken_adet) || 1;
          const unitFields = malzeme.product_type === 'ssh_unit'
            ? { unit_id: '', ssh_unit_id: malzeme.product_id }
            : { unit_id: malzeme.product_id, ssh_unit_id: '' };

          // HM'leri ekle (adet çarpımı ile)
          if (hms) {
            hms.forEach(hm => {
              newMaterials.push({
                malzeme_name: hm.product_name,
                product_id: hm.id,
                product_type: 'HM',
                gereken_adet: (hm.quantity || 1) * unitMultiplier,
                birim: 'adet',
                fiyat: hm.price || 0,
                doviz: hm.currency || 0,
                maliyet_carpani: parseFloat(malzeme.maliyet_carpani) || 1,
                maliyet_carpani_secim: malzeme.maliyet_carpani_secim,
                teslim_periyot_tipi: malzeme.teslim_periyot_tipi,
                teslim_min: malzeme.teslim_min,
                teslim_max: malzeme.teslim_max,
                malzeme_aciklama: malzeme.malzeme_aciklama || '',
                ...unitFields,
                unit_adi: malzeme.malzeme_name,
                unit_adet: unitMultiplier,
                is_valid: true,
                tutar: (hm.price || 0) * (hm.quantity || 1) * unitMultiplier * (parseFloat(malzeme.maliyet_carpani) || 1),
              });
            });
          }

          // SSH_HM'leri ekle (sadece SSH_UNIT için)
          if (ssh_hms) {
            ssh_hms.forEach(ssh_hm => {
              newMaterials.push({
                malzeme_name: ssh_hm.product_name,
                product_id: ssh_hm.id,
                product_type: 'SSH_HM',
                gereken_adet: (ssh_hm.quantity || 1) * unitMultiplier,
                birim: 'adet',
                fiyat: ssh_hm.price || 0,
                doviz: ssh_hm.currency || 0,

                maliyet_carpani: parseFloat(malzeme.maliyet_carpani) || 1,
                maliyet_carpani_secim: malzeme.maliyet_carpani_secim,
                teslim_periyot_tipi: malzeme.teslim_periyot_tipi,
                teslim_min: malzeme.teslim_min,
                teslim_max: malzeme.teslim_max,
                malzeme_aciklama: malzeme.malzeme_aciklama || '',
                ...unitFields,
                unit_adi: malzeme.malzeme_name,
                unit_adet: unitMultiplier,
                is_valid: true,
                tutar: (ssh_hm.price || 0) * (ssh_hm.quantity || 1) * unitMultiplier * (parseFloat(malzeme.maliyet_carpani) || 1),
              });
            });
          }

          // ELK'leri ekle (adet çarpımı ile)
          if (elks) {
            elks.forEach(elk => {
              newMaterials.push({
                malzeme_name: elk.product_name,
                product_id: elk.id,
                product_type: 'ELK',
                gereken_adet: (elk.quantity || 1) * unitMultiplier,
                birim: 'adet',
                fiyat: elk.price || 0,
                doviz: elk.currency || 0,

                maliyet_carpani: parseFloat(malzeme.maliyet_carpani) || 1,
                maliyet_carpani_secim: malzeme.maliyet_carpani_secim,
                teslim_periyot_tipi: malzeme.teslim_periyot_tipi,
                teslim_min: malzeme.teslim_min,
                teslim_max: malzeme.teslim_max,
                malzeme_aciklama: malzeme.malzeme_aciklama || '',
                ...unitFields,
                unit_adi: malzeme.malzeme_name,
                unit_adet: unitMultiplier,
                is_valid: true,
                tutar: (elk.price || 0) * (elk.quantity || 1) * unitMultiplier * (parseFloat(malzeme.maliyet_carpani) || 1),
              });
            });
          }

          // UP'ları ekle (adet çarpımı ile)
          if (ups) {
            ups.forEach(up => {
              newMaterials.push({
                malzeme_name: up.product_name,
                product_id: up.id,
                product_type: 'UP',
                gereken_adet: (up.quantity || 1) * unitMultiplier,
                birim: 'adet',
                fiyat: up.price || 0,
                doviz: up.currency || 0,

                maliyet_carpani: parseFloat(malzeme.maliyet_carpani) || 1,
                maliyet_carpani_secim: malzeme.maliyet_carpani_secim,
                teslim_periyot_tipi: malzeme.teslim_periyot_tipi,
                teslim_min: malzeme.teslim_min,
                teslim_max: malzeme.teslim_max,
                malzeme_aciklama: malzeme.malzeme_aciklama || '',
                ...unitFields,
                unit_adi: malzeme.malzeme_name,
                unit_adet: unitMultiplier,
                is_valid: true,
                tutar: (up.price || 0) * (up.quantity || 1) * unitMultiplier * (parseFloat(malzeme.maliyet_carpani) || 1),
              });
            });
          }

          // Tüm malzemeleri listeye ekle
          setMalzemeList([...malzemeList, ...newMaterials]);
          toast.success(`${malzeme.product_type === 'unit' ? 'UNIT' : 'SSH_UNIT'} patlatıldı: ${newMaterials.length} malzeme eklendi`);

          // Formu sıfırla
          setMalzeme({
            malzeme_name: '',
            product_id: '',
            product_type: '',
            gereken_adet: '',
            birim: '',
            fiyat: '',
            doviz:'',
            maliyet_carpani: 1,
            maliyet_carpani_secim: '1',
            teslim_periyot_tipi: 'is_gunu',
            teslim_min: '',
            teslim_max: '',
            malzeme_aciklama: '',
          });
          setMalzemeOption(null);
        }
      } catch (error) {
        console.error('UNIT/SSH_UNIT malzemeleri alınamadı:', error);
        toast.error('Malzemeler alınırken hata oluştu');
      }
    } else {
      // Normal malzeme ekleme
      const newEntry = {
        ...malzeme,
        is_valid: true,
        tutar: (parseFloat(malzeme.fiyat) || 0) * (parseFloat(malzeme.gereken_adet) || 0) * (parseFloat(malzeme.maliyet_carpani) || 1),
      };

      setMalzemeList([...malzemeList, newEntry]);
      toast.success('Malzeme başarıyla eklendi.');

      // Formu sıfırla
      setMalzeme({
        malzeme_name: '',
        product_id: '',
        product_type: '',
        gereken_adet: '',
        birim: '',
        fiyat: '',
        doviz:'',
        maliyet_carpani: 1,
        maliyet_carpani_secim: '1',
        teslim_periyot_tipi: 'is_gunu',
        teslim_min: '',
        teslim_max: '',
        malzeme_aciklama: '',
      });
      setMalzemeOption(null);
    }
  };

  // Malzeme seçimi
  const handleMalzemeChange = (newValue) => {
    console.log('malzeme',newValue);
    if (!newValue) {
      setMalzeme({
        malzeme_name: '',
        product_id: '',
        product_type: '',
        gereken_adet: '',
        birim: '',
        fiyat: '',
        doviz:'',
        maliyet_carpani: 1,
        maliyet_carpani_secim: '1',
        teslim_periyot_tipi: 'is_gunu',
        teslim_min: '',
        teslim_max: '',
        malzeme_aciklama: '',
      });
      setMalzemeOption(null);
      return;
    }

    // Tüm ürün tipleri için (UNIT, SSH_UNIT dahil) sadece form state'ini doldur
    setMalzeme({
      ...malzeme,
      malzeme_name: newValue.product_name,
      product_id: newValue.id,
      product_type: newValue.product_type,
      fiyat: newValue.price || 0,
      doviz: newValue.currency,
    });

    setMalzemeOption(newValue);
  };

  // Drag & Drop sıralama
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = malzemeList.findIndex((_, i) => `item-${i}` === active.id);
    const newIndex = malzemeList.findIndex((_, i) => `item-${i}` === over.id);
    const newList = arrayMove(malzemeList, oldIndex, newIndex);
    setMalzemeList(newList);

    // Düzenleme modundaysa backend'i güncelle
    if (duzenlemeModu) {
      const siralar = newList
        .filter(item => item.id)
        .map((item, index) => ({ id: item.id, sira_no: index + 1 }));

      if (siralar.length > 0) {
        try {
          await api.patch(`/teklifler/${encodeURIComponent(isEmriData.teklif_no)}/kalemler/sira`, { sira: siralar });
        } catch (err) {
          console.error('Sıra güncellenemedi:', err);
        }
      }
    }
  };

  // Malzeme silme
  const handleExportExcel = () => {
    const rows = malzemeList.map((item) => ({
      'Malzeme Adı': item.malzeme_name || '',
      'Tür': item.product_type || '',
      'Adet': item.gereken_adet || '',
      'Birim': item.birim || '',
      'Birim Fiyat': parseFloat(item.fiyat || 0).toFixed(2),
      'Maliyet Çarpanı': item.maliyet_carpani || '',
      'Tutar': parseFloat(item.tutar || 0).toFixed(2),
      'Teslim': item.teslim_min && item.teslim_max ? `${item.teslim_min}-${item.teslim_max} ${item.teslim_periyot_tipi === 'is_gunu' ? 'İş Günü' : 'Hafta'}` : '',
      'Açıklama': item.malzeme_aciklama || '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Malzeme Listesi');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buf], { type: 'application/octet-stream' }), `malzeme_listesi_${isEmriData.teklif_no}.xlsx`);
  };

  const handleDeleteMaterial = async (index) => {
    const malzemeItem = malzemeList[index];

    // Eğer düzenleme modundaysa ve ürünün ID'si varsa, backend'den de sil
    if (duzenlemeModu && malzemeItem.id) {
      try {
        await api.delete(`/teklifler/${encodeURIComponent(isEmriData.teklif_no)}/kalemler/${malzemeItem.id}`);
        const updatedList = malzemeList.filter((_, i) => i !== index);
        setMalzemeList(updatedList);
        toast.success('Malzeme veritabanından silindi.');
      } catch (error) {
        console.error('Malzeme silme hatası:', error);
        toast.error('Malzeme silinirken hata oluştu.');
      }
    } else {
      // Yeni eklenen malzeme, sadece listeden sil
      const updatedList = malzemeList.filter((_, i) => i !== index);
      setMalzemeList(updatedList);
      toast.info('Malzeme listeden silindi.');
    }
  };

  // Sadece başlık kaydet (malzeme kaydetme)
  const handleSaveHeaderOnly = async (e) => {
    e.preventDefault();
    if (!isEmriData.musteri || !isEmriData.lokasyon) {
      toast.error('Müşteri ve lokasyon seçimi zorunludur.');
      return;
    }
    try {
      const teklifNo = isEmriData.teklif_no;
      await saveTeklifBaslik();
      for (const [slot, file] of [['dosya', dosya1], ['dosya_iki', dosya2], ['dosya_uc', dosya3]]) {
        if (!file) continue;
        const fd = new FormData();
        fd.append('dosya', file);
        fd.append('slot', slot);
        await api.post(`/teklifler/${encodeURIComponent(teklifNo)}/dosyalar`, fd);
      }
      toast.success('Teklif başlığı başarıyla kaydedildi!');
    } catch (error) {
      console.error('Başlık kaydedilirken hata:', error);
      toast.error('Başlık kaydedilirken hata oluştu: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Hem başlık hem de malzemeleri kaydet
  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log('Tam kayıt başlıyor - isEmriData:', isEmriData);
    console.log('Müşteri:', isEmriData.musteri);
    console.log('Lokasyon:', isEmriData.lokasyon);
    console.log('Malzeme sayısı:', malzemeList);

    if (!isEmriData.musteri || !isEmriData.lokasyon) {
      toast.error('Müşteri ve lokasyon seçimi zorunludur.');
      return;
    }

    if (malzemeList.length === 0) {
      toast.error('En az bir malzeme eklemelisiniz.');
      return;
    }

    setIsSubmitting(true);
    try {
      // 1. Önce teklif başlığını kaydet
      const baslikFormData = new FormData();

      baslikFormData.append('teklif_no', isEmriData.teklif_no);
      baslikFormData.append('teklif_tarihi', isEmriData.teklif_tarihi);
      baslikFormData.append('doviz', isEmriData.doviz);
      baslikFormData.append('musteri', isEmriData.musteri);
      baslikFormData.append('lokasyon', isEmriData.lokasyon);
      baslikFormData.append('makine', isEmriData.makine || '');

      baslikFormData.append('yetkili', isEmriData.yetkili || '');
      baslikFormData.append('yetkili_eposta', isEmriData.yetkili_eposta || '');
      baslikFormData.append('yetkili_cep_tel', isEmriData.yetkili_cep_tel || '');
      baslikFormData.append('teklif_dili', isEmriData.teklif_dili || 'Türkçe');
      baslikFormData.append('musteri_siparis_no', isEmriData.musteri_siparis_no || '');
      baslikFormData.append('oto_mail', isEmriData.oto_mail || 0);
      baslikFormData.append('oto_iptal', isEmriData.oto_iptal || 0);
      baslikFormData.append('teslim_sekli', isEmriData.teslim_sekli || '');
      baslikFormData.append('nakliye_firmasi', isEmriData.nakliye_firmasi || '');
      baslikFormData.append('odeme_sekli', isEmriData.odeme_sekli || '');
      baslikFormData.append('temsilci', localStorage.getItem('ssh_user_name') || '');

      // Eski PHP alanları teklif_tipi seciminden turetiliyor.
      baslikFormData.append('not1', legacyTeklifFields.not1 ? 'İlgili ürünler müşteri memnuniyeti kapsamında gönderiliyor.' : '');
      baslikFormData.append('garanti', legacyTeklifFields.garanti ? '1' : '');
      baslikFormData.append('sozlesme', legacyTeklifFields.sozlesme ? '1' : '');
      baslikFormData.append('rektifiye', legacyTeklifFields.rektifiye ? '1' : '0');
      baslikFormData.append('yedek_parca', legacyTeklifFields.yedek_parca ? '1' : '0');
      baslikFormData.append('teklif_tipi', selectedTeklifTipi);
      baslikFormData.append('not2', isEmriData.not2 || '');
      baslikFormData.append('aciklama', isEmriData.aciklama || '');

      // Dosyaları ekle
      if (dosya1) baslikFormData.append('dosya', dosya1);
      if (dosya2) baslikFormData.append('dosya_iki', dosya2);
      if (dosya3) baslikFormData.append('dosya_uc', dosya3);

      console.log('Başlık kaydediliyor...', duzenlemeModu ? 'PATCH' : 'POST');
      const baslikResponse = await saveTeklifBaslik();

      console.log('Başlık kayıt yanıtı:', baslikResponse.data);

      // 2. Sonra yeni eklenen malzemeleri kaydet (id olmayan malzemeler)
      // Düzenleme modunda id olan malzemeler zaten inline update ile güncellenmiş olacak
      console.log('Düzenleme Modu:', duzenlemeModu);
      console.log('Tüm Malzeme Listesi:', malzemeList);
      console.log('Malzeme ID\'leri:', malzemeList.map(item => ({
        name: item.malzeme_name,
        id: item.id,
        hasId: !!item.id
      })));

      const yeniMalzemeler = malzemeList
        .map((item, index) => ({ ...item, _sira: index + 1, _listIndex: index }))
        .filter(item => !item.id);

      console.log('Yeni Malzemeler (ID olmayan):', yeniMalzemeler);

      if (yeniMalzemeler.length > 0) {
        console.log(`${yeniMalzemeler.length} adet yeni malzeme kaydedilecek...`);
        const updatedList = [...malzemeList];

        for (let i = 0; i < yeniMalzemeler.length; i++) {
          const malzemeItem = yeniMalzemeler[i];
          console.log(`Malzeme ${i + 1}/${yeniMalzemeler.length} kaydediliyor:`, malzemeItem.malzeme_name);

          const malzemeFormData = new FormData();

        malzemeFormData.append('siparis_nok', isEmriData.teklif_no);
        malzemeFormData.append('teklif_tarihi', isEmriData.teklif_tarihi);
        malzemeFormData.append('urun_id', malzemeItem.product_id || '0');
        malzemeFormData.append('urun_tipi', malzemeItem.product_type || '');
        malzemeFormData.append('not', malzemeItem.malzeme_aciklama || '');
        malzemeFormData.append('referans', '');
        malzemeFormData.append('ilgiliKisi', '');
        malzemeFormData.append('parca_teslim_tarihi', ''); // Backend hesaplayacak

        // Maliyet çarpanı
        if (malzemeItem.maliyet_carpani_secim === 'diger') {
          malzemeFormData.append('maliyet_carpani', '0');
          malzemeFormData.append('digerMaliyet', malzemeItem.maliyet_carpani || '1');
        } else {
          malzemeFormData.append('maliyet_carpani', malzemeItem.maliyet_carpani || '1');
          malzemeFormData.append('digerMaliyet', '');
        }

        malzemeFormData.append('adet', malzemeItem.gereken_adet || '0');
        // Birim fiyatı gönder (backend adet ve maliyet çarpanıyla hesaplayacak)
        malzemeFormData.append('fiyat', malzemeItem.fiyat || '0');
        malzemeFormData.append('urun_adi', malzemeItem.malzeme_name || '');
          malzemeFormData.append('doviz', malzemeItem.doviz || '');

          malzemeFormData.append('son_id', '');
        malzemeFormData.append('carpan', '');

        // Boyut bilgileri (frontend'de yok, boş gönder)
        malzemeFormData.append('discap', '');
        malzemeFormData.append('iccap', '');
        malzemeFormData.append('ccboy', '');
        malzemeFormData.append('cbdiscap', '');
        malzemeFormData.append('cboy', '');
        malzemeFormData.append('en', '');
        malzemeFormData.append('boy', '');
        malzemeFormData.append('yukseklik', '');
        malzemeFormData.append('boym', '');
        malzemeFormData.append('agirlik', '');

        malzemeFormData.append('birim', malzemeItem.birim || 'adet');
        malzemeFormData.append('sira_no', malzemeItem._sira || 0);
        malzemeFormData.append('tes_tar_bas', malzemeItem.teslim_min || '');
        malzemeFormData.append('tes_tar_bit', malzemeItem.teslim_max || '');

        // is_gunu -> gun, hafta -> hafta
        const teslimatTuru = malzemeItem.teslim_periyot_tipi === 'is_gunu' ? 'gun' : 'hafta';
        malzemeFormData.append('tes_tar_tur', teslimatTuru);

        malzemeFormData.append('doviz', malzemeItem.doviz);

        // UNIT bilgilerini ekle (eğer varsa)
        malzemeFormData.append('unit_id', malzemeItem.unit_id || '');
        malzemeFormData.append('ssh_unit_id', malzemeItem.ssh_unit_id || '');
        malzemeFormData.append('unit_adi', malzemeItem.unit_adi || '');
        malzemeFormData.append('unit_adet', malzemeItem.unit_adet || '');

          const malzemeResponse = await api.post(`/teklifler/${encodeURIComponent(isEmriData.teklif_no)}/kalemler`, {
            urun_id: malzemeItem.product_id ? Number(malzemeItem.product_id) : null,
            urun_tipi: malzemeItem.product_type || null,
            urun_adi: malzemeItem.malzeme_name || null,
            aciklama: malzemeItem.malzeme_aciklama || null,
            fiyat: parseFloat(malzemeItem.fiyat) || 0,
            doviz: malzemeItem.doviz || 'TL',
            adet: parseInt(malzemeItem.gereken_adet, 10) || 1,
            maliyet_carpani: parseFloat(malzemeItem.maliyet_carpani) || 1,
            birim: malzemeItem.birim || 'adet',
            teslim_tarihi_baslangic: malzemeItem.teslim_min || null,
            teslim_tarihi_bitis: malzemeItem.teslim_max || null,
            teslim_tarihi_turu: malzemeItem.teslim_periyot_tipi === 'is_gunu' ? 'gun' : 'hafta',
            unit_id: malzemeItem.unit_id ? Number(malzemeItem.unit_id) : null,
            ssh_unit_id: malzemeItem.ssh_unit_id ? Number(malzemeItem.ssh_unit_id) : null,
            unit_adi: malzemeItem.unit_adi || null,
            unit_adet: malzemeItem.unit_adet ? Number(malzemeItem.unit_adet) : null,
          });

          console.log(`Malzeme ${i + 1} kayıt yanıtı:`, malzemeResponse.data);
          if (malzemeResponse.data?.id) {
            updatedList[malzemeItem._listIndex] = { ...updatedList[malzemeItem._listIndex], id: malzemeResponse.data.id };
          }
        }
        setMalzemeList(updatedList);

        // Kayıt sonrası tüm listenin sıra numaralarını DB ile senkronla
        const siralar = updatedList
          .filter(item => item.id)
          .map((item, index) => ({ id: item.id, sira_no: index + 1 }));
        if (siralar.length > 0) {
          try {
            await api.patch(`/teklifler/${encodeURIComponent(isEmriData.teklif_no)}/kalemler/sira`, { sira: siralar });
          } catch (err) {
            console.error('Sıra güncellenemedi:', err);
          }
        }
      } else {
        console.log('Yeni malzeme yok, sadece başlık güncellendi.');
      }

      console.log('Tüm işlemler tamamlandı!');
      toast.success('Teklif ve malzemeler başarıyla kaydedildi!');

      setTimeout(() => {
        navigate("/hazirlananlar", { replace: true });
      }, 1500);

    } catch (error) {
      console.error('Form gönderilirken hata:', error);
      console.error('Hata detayı:', error.response?.data);
      toast.error('Form gönderilirken hata oluştu: ' + (error.response?.data || error.message));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Lokasyon ekleme modalı
  const handleLokasyonEkle = async () => {
    try {
      await api.post('/lookups/lokasyonlar', {
        musteri_id: Number(newLokasyon.musteri_id) || null,
        ulke: newLokasyon.ulke || null,
        sehir: newLokasyon.sehir || null,
        lokasyon_adi: newLokasyon.lokasyon_adi,
        telefon: newLokasyon.telefon || null,
        yetkili: newLokasyon.yetkili || null,
        adres: newLokasyon.adres || null,
      });
      toast.success('Lokasyon başarıyla eklendi!');
      setOpenLokasyonModal(false);
      fetchMusteriler();
      setNewLokasyon({
        musteri_id: '',
        ulke: '',
        sehir: '',
        lokasyon_adi: '',
        telefon: '',
        yetkili: '',
        adres: '',
        makineler: [],
      });
    } catch (error) {
      console.error('Lokasyon eklenirken hata:', error);
      toast.error('Lokasyon eklenirken hata oluştu.');
    }
  };

  const birimOptions = [
    { value: 'adet', label: 'Adet' },
    { value: 'boy', label: 'Boy' },
    { value: 'koli', label: 'Koli' },
    { value: 'metre', label: 'Metre' },
    { value: 'kg', label: 'Kg' },
    { value: 'set', label: 'Set' },
    { value: 'paket', label: 'Paket' },
  ];

  // React-Select için custom styles (z-index sorunu ve boyut için)
  const selectStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: '36px',
      height: 'auto',
      fontSize: '0.875rem',
    }),
    valueContainer: (provided) => ({
      ...provided,
      height: 'auto',
      flexWrap: 'wrap',
      padding: '2px 8px',
    }),
    multiValue: (provided) => ({
      ...provided,
      maxWidth: '100%',
    }),
    input: (provided) => ({
      ...provided,
      margin: '0px',
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: 'auto',
      alignSelf: 'stretch',
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
      fontSize: '0.875rem',
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
    option: (provided) => ({
      ...provided,
      padding: '6px 12px',
    }),
  };

  return (
    <Box className="ct-teklif-form" sx={{ p: 2 }}>
      <ToastContainer position="top-right" autoClose={3000} />

      <Typography className="ct-accent" variant="h5" gutterBottom sx={{ mb: 2, fontWeight: 'bold', color: '#e59e53' }}>
        {duzenlemeModu ? `Teklif Düzenle - ${isEmriData.teklif_no}` : 'Teklif Ekle'}
      </Typography>

      <Button
        variant="contained"
        size="small"
        onClick={() => setOpenLokasyonModal(true)}
        sx={{
          mb: 2,
          backgroundColor: '#e59e53',
          '&:hover': {
            backgroundColor: '#d18a43',
          }
        }}
      >
        Lokasyon Ekle
      </Button>

      <form
        onSubmit={handleSubmit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
          }
        }}
      >
        <Paper sx={{ p: 1, mb: 1.5, bgcolor: '#FFB413' }}>
          <Typography variant="h6" gutterBottom sx={{ fontSize: '0.9rem', fontWeight: 'bold', mb: 0.5 }}>
            Teklif Bilgileri
          </Typography>

          <Grid container spacing={0.75}>
            {/* İlk Satır */}
            <Grid item xs={12} md={1.5}>
              <TextField
                fullWidth
                size="small"
                label="Teklif No"
                value={isEmriData.teklif_no}
                InputProps={{ readOnly: true }}
                required
              />
            </Grid>

            <Grid item xs={12} md={1.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Para Birimi</InputLabel>
                <MuiSelect
                  value={isEmriData.doviz}
                  onChange={(e) => {
                    setIsEmriData((prev) => ({ ...prev, doviz: e.target.value }));
                    // Düzenleme modunda kaydet

                  }}
                  label="Para Birimi"
                >
                  <MenuItem value="EURO">EURO</MenuItem>
                  <MenuItem value="TL">TL</MenuItem>
                </MuiSelect>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={1.5}>
              <TextField
                fullWidth
                size="small"
                label="Teklif Tarihi"
                type="date"
                value={isEmriData.teklif_tarihi}
                onChange={(e) => setIsEmriData((prev) => ({ ...prev, teklif_tarihi: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>

            <Grid item xs={12} md={1.5}>
              <Select
                placeholder="Müşteri"
                options={musteriler.map(m => ({
                  value: `${m.musteri_id}*${m.firma}`,
                  label: m.firma
                }))}
                value={selectedMusteri}
                onChange={handleMusteriChange}
                isClearable
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </Grid>

            <Grid item xs={12} md={1.5}>
              <Select
                placeholder="Lokasyon"
                options={lokasyonlar.map(l => ({
                  value: `${l.lokasyon_id}*${l.lokasyon_adi}`,
                  label: l.lokasyon_adi
                }))}
                value={selectedLokasyon}
                onChange={handleLokasyonChange}
                isClearable
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <Select
                placeholder="Makine"
                isMulti
                options={makineler.map(m => ({
                  value: `${m.siparis_no}*${m.seri_no}*${m.makine}`,
                  label: `${m.siparis_no}-${m.seri_no}-${m.makine}`
                }))}
                value={selectedMakineler}
                onChange={(selected) => {
                  const list = selected || [];
                  setSelectedMakineler(list);
                  setIsEmriData((prev) => ({
                    ...prev,
                    makine: list.map((o) => o.value).join(','),
                  }));
                }}
                isClearable
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <Select
                placeholder="Yetkili"
                options={yetkililer}
                value={selectedYetkili}
                onChange={(selected) => {
                  setSelectedYetkili(selected);
                  if (selected) {
                    setIsEmriData({
                      ...isEmriData,
                      yetkili: selected.yetkili,
                      yetkili_eposta: selected.eposta,
                      yetkili_cep_tel: selected.telefon
                    });
                  }
                }}
                isClearable
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </Grid>



            {/* İkinci Satır */}
            <Grid item xs={12} md={1.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Teklif Dili</InputLabel>
                <MuiSelect
                  value={displayDil(isEmriData.teklif_dili)}
                  onChange={(e) =>
                    setIsEmriData({
                      ...isEmriData,
                      teklif_dili: e.target.value,
                      ...dilTicariVarsayilan(e.target.value),
                    })
                  }
                  label="Teklif Dili"
                >
                  <MenuItem value="Türkçe">Türkçe</MenuItem>
                  <MenuItem value="English">İngilizce</MenuItem>
                </MuiSelect>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Müşteri Sipariş No"
                value={isEmriData.musteri_siparis_no}
                onChange={(e) => setIsEmriData({ ...isEmriData, musteri_siparis_no: e.target.value })}
                placeholder="Müşteri sipariş no"
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Yetkili E-posta"
                type="email"
                value={isEmriData.yetkili_eposta}
                onChange={(e) => setIsEmriData({ ...isEmriData, yetkili_eposta: e.target.value })}
                placeholder="Yetkili eposta adresi"
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Yetkili Telefon"
                value={isEmriData.yetkili_cep_tel}
                onChange={(e) => setIsEmriData({ ...isEmriData, yetkili_cep_tel: e.target.value })}
                placeholder="Yetkili telefon numarası"
              />
            </Grid>

            <Grid item xs={12} md={1.5}>
              <Box
                component="button"
                type="button"
                onClick={() => setIsEmriData({
                  ...isEmriData,
                  oto_mail: isEmriData.oto_mail == 1 ? 0 : 1,
                })}
                sx={compactToggleSx(isEmriData.oto_mail == 1)}
              >
                <NotificationsActiveIcon />
                O. Hatırlat
              </Box>
            </Grid>
            <Grid item xs={12} md={1.5}>
              <Box
                component="button"
                type="button"
                onClick={() => setIsEmriData({
                  ...isEmriData,
                  oto_iptal: isEmriData.oto_iptal == 1 ? 0 : 1,
                })}
                sx={compactToggleSx(isEmriData.oto_iptal == 1)}
              >
                <EmailIcon />
                O. İptal
              </Box>
            </Grid>
            <Grid item xs={12} md={1.5}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}>Ek</Typography>
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                  <input
                      type="file"
                      id="dosya1"
                      onChange={(e) => setDosya1(e.target.files[0])}
                      style={{ display: 'none' }}
                  />
                  <label htmlFor="dosya1">
                    <Button
                        component="span"
                        size="small"
                        variant="outlined"
                        sx={{ minWidth: '30px', p: 0.5, fontSize: '0.7rem' }}
                    >
                      {dosya1 ? '1✓' : '1'}
                    </Button>
                  </label>

                  <input
                      type="file"
                      id="dosya2"
                      onChange={(e) => setDosya2(e.target.files[0])}
                      style={{ display: 'none' }}
                  />
                  <label htmlFor="dosya2">
                    <Button
                        component="span"
                        size="small"
                        variant="outlined"
                        sx={{ minWidth: '30px', p: 0.5, fontSize: '0.7rem' }}
                    >
                      {dosya2 ? '2✓' : '2'}
                    </Button>
                  </label>

                  <input
                      type="file"
                      id="dosya3"
                      onChange={(e) => setDosya3(e.target.files[0])}
                      style={{ display: 'none' }}
                  />
                  <label htmlFor="dosya3">
                    <Button
                        component="span"
                        size="small"
                        variant="outlined"
                        sx={{ minWidth: '30px', p: 0.5, fontSize: '0.7rem' }}
                    >
                      {dosya3 ? '3✓' : '3'}
                    </Button>
                  </label>

                  {dosya1 && (
                      <IconButton
                          size="small"
                          onClick={() => setDosya1(null)}
                          sx={{ p: 0.25 }}
                      >
                        <DeleteIcon sx={{ fontSize: '0.9rem' }} />
                      </IconButton>
                  )}
                  {dosya2 && (
                      <IconButton
                          size="small"
                          onClick={() => setDosya2(null)}
                          sx={{ p: 0.25 }}
                      >
                        <DeleteIcon sx={{ fontSize: '0.9rem' }} />
                      </IconButton>
                  )}
                  {dosya3 && (
                      <IconButton
                          size="small"
                          onClick={() => setDosya3(null)}
                          sx={{ p: 0.25 }}
                      >
                        <DeleteIcon sx={{ fontSize: '0.9rem' }} />
                      </IconButton>
                  )}
                </Box>
              </Box>
            </Grid>


            {/* Üçüncü Satır - Detaylı Bilgiler ve Kaydet Butonları */}
            <Grid item xs={12} md={9}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Teslim Şekli"
                    value={isEmriData.teslim_sekli}
                    onChange={(e) => setIsEmriData({ ...isEmriData, teslim_sekli: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Nakliye Firması"
                    value={isEmriData.nakliye_firmasi}
                    onChange={(e) => setIsEmriData({ ...isEmriData, nakliye_firmasi: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Ödeme Şekli"
                    value={isEmriData.odeme_sekli}
                    onChange={(e) => setIsEmriData({ ...isEmriData, odeme_sekli: e.target.value })}
                  />
	                </Grid>
                <Grid item xs={12}>
                  <Box
                    component="fieldset"
                    sx={{
                      border: '1px solid rgba(95, 74, 23, 0.45)',
                      borderRadius: '7px',
                      m: 0,
                      px: 0.8,
                      pt: 1.2,
                      pb: 0.8,
                      minWidth: 0,
                    }}
                  >
                    <Box component="legend" sx={{ width: 'auto', float: 'none', px: 0.7, fontSize: '0.75rem', fontWeight: 700, color: '#5f4a17' }}>
                      Teklif Tipi
                    </Box>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: 'minmax(170px, 0.72fr) minmax(0, 2.28fr)' },
                        gap: 0.7,
                      }}
                    >
                      {teklifTipOptionGroups.map((group) => {
                        const groupOptions = teklifTipOptions.filter((option) => group.keys.includes(option.key));
                        return (
                          <Box
                            key={group.title}
                            component="fieldset"
                            sx={{
                              border: '1px solid rgba(95, 74, 23, 0.45)',
                              borderRadius: '7px',
                              m: 0,
                              px: 0.55,
                              pt: 1.05,
                              pb: 0.55,
                              minWidth: 0,
                            }}
                          >
                            <Box
                              component="legend"
                              sx={{
                                width: 'auto',
                                float: 'none',
                                px: 0.6,
                                fontSize: '0.72rem',
                                fontWeight: 700,
                                color: '#5f4a17',
                              }}
                            >
                              {group.title}
                            </Box>
                            <Box
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: group.columns,
                                gap: 0.4,
                              }}
                            >
                              {groupOptions.map((option) => {
                                const active = selectedTeklifTipi === option.key;
                                return (
                                  <Tooltip
                                    key={option.key}
                                    title={option.tooltip || option.label}
                                    arrow
                                    placement="top"
                                    componentsProps={{
                                      tooltip: {
                                        sx: {
                                          maxWidth: 420,
                                          fontSize: 13.5,
                                          fontWeight: 600,
                                          lineHeight: 1.45,
                                          bgcolor: '#2f2410',
                                          color: '#fff8e7',
                                          px: 1.2,
                                          py: 0.9,
                                        },
                                      },
                                    }}
                                  >
                                    <Box
                                      component="button"
                                      type="button"
                                      onClick={() => handleTeklifTipiChange(option.key)}
                                      sx={teklifTipTileSx(active)}
                                    >
                                      {option.icon}
                                      <Box
                                        component="span"
                                        sx={{
                                          minWidth: 0,
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap',
                                          fontSize: 11,
                                          fontWeight: 700,
                                          lineHeight: 1,
                                          textAlign: 'left',
                                        }}
                                      >
                                        {option.label}
                                      </Box>
                                    </Box>
                                  </Tooltip>
                                );
                              })}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    component="fieldset"
                    sx={{
                      border: '1px solid #8a6a24',
                      borderRadius: '7px',
                      m: 0,
                      px: 0.9,
                      pt: 1.25,
                      pb: 0.9,
                      minWidth: 0,
                    }}
                  >
                    <Box component="legend" sx={{ width: 'auto', float: 'none', px: 0.7, fontSize: '0.75rem', fontWeight: 700, color: '#5f4a17' }}>
                      Not
                    </Box>
                    <TextField
                      fullWidth
                      size="small"
                      multiline
                      rows={4}
                      value={isEmriData.not2}
                      onChange={(e) => setIsEmriData({ ...isEmriData, not2: e.target.value })}
                      placeholder="Buraya PDF'lerde görülecek açıklamayı giriniz."
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          bgcolor: '#fff',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#8a6a24',
                        },
                        '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#6f551d',
                        },
                        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#5f4a17',
                          borderWidth: 1,
                        },
                        '& textarea': {
                          lineHeight: 1.35,
                        },
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Grid>

	          </Grid>

        </Paper>

        {/* MALZEME EKLEME BÖLÜMÜ */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography className="ct-accent" variant="h6" gutterBottom sx={{ fontSize: '1rem', fontWeight: 'bold', color: '#e59e53', mb: 1 }}>
            Malzeme Ekle
          </Typography>



          <Grid container spacing={1.5}>
            <Grid item xs={12} md={3}>
              <AsyncSelect
                cacheOptions
                defaultOptions={false}
                loadOptions={searchProducts}
                value={malzemeOption}
                onChange={handleMalzemeChange}
                placeholder="Ürün ara... (min 3 karakter)"
                noOptionsMessage={({ inputValue }) =>
                  !inputValue ? "Aramak için en az 3 karakter girin" :
                    inputValue.length < 3 ? "En az 3 karakter gerekli" :
                      "Sonuç bulunamadı"
                }
                isClearable
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </Grid>



            <Grid item xs={12} md={1}>
              <TextField
                fullWidth
                type="number"
                label="Gereken Adet"
                value={malzeme.gereken_adet}
                onChange={(e) => setMalzeme({ ...malzeme, gereken_adet: e.target.value })}
                size="small"
              />
            </Grid>

            <Grid item xs={12} md={1}>
              <Select
                placeholder="Birim"
                options={birimOptions}
                value={malzeme.birim ? birimOptions.find(opt => opt.value === malzeme.birim) : null}
                onChange={(selected) => setMalzeme({ ...malzeme, birim: selected?.value || '' })}
                isClearable
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </Grid>

            <Grid item xs={12} md={1}>
              <TextField
                fullWidth
                type="number"
                label="Birim Fiyat"
                value={malzeme.fiyat}
                onChange={(e) => setMalzeme({ ...malzeme, fiyat: e.target.value })}
                size="small"
                InputProps={{
                  readOnly: !malzeme.fiyat || parseFloat(malzeme.fiyat) === 0,
                }}
                error={!malzeme.fiyat || parseFloat(malzeme.fiyat) === 0}
                helperText={(!malzeme.fiyat || parseFloat(malzeme.fiyat) === 0) ? 'Fiyatı yok, kontrol ediniz' : ''}
                sx={{
                  '& .MuiInputBase-input': {
                    backgroundColor: (!malzeme.fiyat || parseFloat(malzeme.fiyat) === 0) ? '#fff3cd' : 'inherit',
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={1}>
              <Select
                placeholder="Maliyet Çarpanı"
                options={maliyetCarpaniOptions}
                value={maliyetCarpaniOptions.find(opt => opt.value === malzeme.maliyet_carpani_secim)}
                onChange={(selected) => {
                  if (selected?.value === 'diger') {
                    setMalzeme({
                      ...malzeme,
                      maliyet_carpani_secim: 'diger',
                      maliyet_carpani: ''
                    });
                  } else {
                    setMalzeme({
                      ...malzeme,
                      maliyet_carpani_secim: selected?.value || '1',
                      maliyet_carpani: selected?.value || 1
                    });
                  }
                }}
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </Grid>

            {malzeme.maliyet_carpani_secim === 'diger' && (
              <Grid item xs={12} md={1}>
                <TextField
                  fullWidth
                  type="number"
                  label="Çarpan"
                  placeholder="1.25"
                  value={malzeme.maliyet_carpani}
                  onChange={(e) => setMalzeme({ ...malzeme, maliyet_carpani: e.target.value })}
                  size="small"
                  inputProps={{ step: 0.1}}
                />
              </Grid>
            )}

            <Grid item xs={12} md={1}>
              <TextField
                fullWidth
                label="Tutar"
                value={((parseFloat(malzeme.fiyat) || 0) * (parseFloat(malzeme.gereken_adet) || 0) * (parseFloat(malzeme.maliyet_carpani) || 1)).toFixed(2)}
                disabled
                size="small"
                sx={{
                  '& .MuiInputBase-input': {
                    fontWeight: 'bold',
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={1}>
              <Select
                placeholder="Periyot"
                options={[
                  { value: 'is_gunu', label: 'İş Günü' },
                  { value: 'hafta', label: 'Hafta' },
                ]}
                value={{
                  value: malzeme.teslim_periyot_tipi,
                  label: malzeme.teslim_periyot_tipi === 'is_gunu' ? 'İş Günü' : 'Hafta'
                }}
                onChange={(selected) => setMalzeme({ ...malzeme, teslim_periyot_tipi: selected?.value || 'is_gunu' })}
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </Grid>

            <Grid item xs={12} md={1}>
              <TextField
                fullWidth
                type="number"
                label="Min"
                value={malzeme.teslim_min}
                onChange={(e) => setMalzeme({ ...malzeme, teslim_min: e.target.value })}
                size="small"
              />
            </Grid>

            <Grid item xs={12} md={1}>
              <TextField
                fullWidth
                type="number"
                label="Max"
                value={malzeme.teslim_max}
                onChange={(e) => setMalzeme({ ...malzeme, teslim_max: e.target.value })}
                size="small"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Açıklama"
                value={malzeme.malzeme_aciklama}
                onChange={(e) => setMalzeme({ ...malzeme, malzeme_aciklama: e.target.value })}
                size="small"
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                size="small"
                type="button"
                variant="contained"
                onClick={handleAddMaterial}
                sx={{
                  backgroundColor: '#e59e53',
                  '&:hover': {
                    backgroundColor: '#d18a43',
                  }
                }}
              >
                Malzeme Ekle
              </Button>
            </Grid>
          </Grid>

          {/* MALZEME LİSTESİ */}
          {malzemeList.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, mb: 0.5 }}>
              <Typography className="ct-accent" variant="h6" sx={{ fontSize: '1rem', fontWeight: 'bold', color: '#e59e53' }}>
                Malzeme Listesi
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={handleExportExcel}
                sx={{ fontSize: '0.75rem', borderColor: '#1f7244', color: '#1f7244', '&:hover': { borderColor: '#155a33', backgroundColor: '#e8f5e9' } }}
              >
                Excel İndir
              </Button>
            </Box>
          )}
          {malzemeList.length > 0 && (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <TableContainer sx={{ mt: 0 }}>
              <Table size="small">
                <TableHead>
                  <TableRow className="ct-accent-bg" sx={{ backgroundColor: '#e59e53' }}>
                    <TableCell sx={{ width: 32, p: 0.5 }} />
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.85rem', p: 1 }}>Malzeme Adı</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.85rem', p: 1 }}>Tür</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.85rem', p: 1 }}>Adet</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.85rem', p: 1 }}>Birim</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.85rem', p: 1 }}>Birim Fiyat</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.85rem', p: 1 }}>Maliyet Çarpanı</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.85rem', p: 1 }}>Tutar</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.85rem', p: 1 }}>Teslim</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.85rem', p: 1 }}>Açıklama</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.85rem', p: 1 }}>Sil</TableCell>
                  </TableRow>
                </TableHead>
                  <SortableContext items={malzemeList.map((_, i) => `item-${i}`)} strategy={verticalListSortingStrategy}>
                    <TableBody>
                      {malzemeList.map((item, index) => (
                        <SortableRow
                          key={index}
                          id={`item-${index}`}
                          item={item}
                          index={index}
                          duzenlemeModu={duzenlemeModu}
                          malzemeList={malzemeList}
                          setMalzemeList={setMalzemeList}
                          updateAdet={updateAdet}
                          updateMaliyetCarpani={updateMaliyetCarpani}
                          updateTarihAraligi={updateTarihAraligi}
                          updateAciklama={updateAciklama}
                          handleDeleteMaterial={handleDeleteMaterial}
                        />
                      ))}
                    </TableBody>
                  </SortableContext>
              </Table>
            </TableContainer>
            </DndContext>
          )}
        </Paper>

        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
          <Button
            type="submit"
            variant="contained"
            size="small"
            disabled={isSubmitting}
            sx={{
              backgroundColor: '#e59e53',
              '&:hover': {
                backgroundColor: '#d18a43',
              },
              px: 4,
              py: 1
            }}
          >
            {isSubmitting ? 'Kaydediliyor...' : (duzenlemeModu ? 'Teklifi Güncelle' : 'Teklif Oluştur')}
          </Button>
        </Box>
      </form>

      {/* LOKASYON EKLEME MODALI */}
      <Dialog className="ct-teklif-form" open={openLokasyonModal} onClose={() => setOpenLokasyonModal(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#e59e53' }}>Yeni Lokasyon Ekle</DialogTitle>
        <DialogContent>
          <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={6}>
              <Select
                placeholder="Müşteri Seçiniz"
                options={musteriler.map(m => ({
                  value: m.musteri_id,
                  label: m.firma
                }))}
                onChange={(selected) => setNewLokasyon({ ...newLokasyon, musteri_id: selected?.value || '' })}
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Select
                placeholder="Makine Seçiniz"
                options={makineler.map(m => ({
                  value: m.siparis_no,
                  label: `${m.siparis_no}-${m.seri_no}-${m.makine}`
                }))}
                onChange={(selected) => setNewLokasyon({ ...newLokasyon, makineler: selected })}
                isMulti
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Ülke"
                value={newLokasyon.ulke}
                onChange={(e) => setNewLokasyon({ ...newLokasyon, ulke: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Şehir"
                value={newLokasyon.sehir}
                onChange={(e) => setNewLokasyon({ ...newLokasyon, sehir: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Lokasyon Adı"
                value={newLokasyon.lokasyon_adi}
                onChange={(e) => setNewLokasyon({ ...newLokasyon, lokasyon_adi: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Telefon"
                value={newLokasyon.telefon}
                onChange={(e) => setNewLokasyon({ ...newLokasyon, telefon: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Yetkili"
                value={newLokasyon.yetkili}
                onChange={(e) => setNewLokasyon({ ...newLokasyon, yetkili: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                multiline
                rows={2}
                label="Adres"
                value={newLokasyon.adres}
                onChange={(e) => setNewLokasyon({ ...newLokasyon, adres: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setOpenLokasyonModal(false)}>İptal</Button>
          <Button
            size="small"
            onClick={handleLokasyonEkle}
            variant="contained"
            sx={{
              backgroundColor: '#e59e53',
              '&:hover': {
                backgroundColor: '#d18a43',
              }
            }}
          >
            Kaydet
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default IsEmriMalzemeEkle;
